import { useQuery } from '@tanstack/react-query';

import ExternalService from '../services/ExternalService/ExternalApiService';
import { type Rule } from '../services/types';

interface ErrorResponse {
  message?: string;
  errorMessage?: string;
}

export class ApiError extends Error {
  status: number;

  errorMessage: string;

  constructor(message: string, status: number, errorMessage: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorMessage = errorMessage;
    Object.setPrototypeOf(this, ApiError.prototype); // Ensure proper inheritance
  }
}

interface ApiResponse {
  statusText: string;
  success: boolean; // Indicates if the API call was successful
  errorMessage: string; // Optional message from the server
  data: Rule[]; // The actual data returned by the API
  error: ErrorResponse; // Error details in case of failure
  status: number;
  ok?: boolean;
}

/**
 * Fetches the list of rules from the external API service.
 *
 * @async
 * @function fetchRules
 * @returns {Promise<Rule[]>} A promise that resolves to an array of rules.
 */
const fetchRules = async (): Promise<Rule[]> => {
  const response = (await ExternalService.getRules()) as ApiResponse;

  if (!response.data && !response.ok) {
    throw new ApiError(
      `Error ${response.errorMessage}: ${response.statusText}`,
      response.status,
      response.errorMessage
    );
  }

  return response.data;
};

/**
 * Custom hook to retrieve the list of rules using React Query's `useQuery`.
 *
 * @function useGetRules
 * @returns {import('@tanstack/react-query').UseQueryResult<Rule[], unknown>} A query result object containing the list of rules, loading state, and potential errors.
 */
const useGetRules = () =>
  useQuery<Rule[], ApiError>({
    queryKey: ['RULES_RESPONSE'], // Unique query key
    queryFn: fetchRules, // Function to fetch data
    staleTime: 0,
    retry: 0,
    refetchOnWindowFocus: false,
  });

export default useGetRules;
